import { Button, DialogTitle, Text } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiTrash2 } from "react-icons/fi";
import { FaMinus } from "react-icons/fa";

import { TablesService } from "@/client";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog";
import useCustomToast from "@/hooks/useCustomToast";

interface DeleteTableProps {
  ids?: string[];
  id?: string;
  disabled?: boolean;
  onSuccess?: () => void;
  variant?: "ghost" | "action";
}

const DeleteTable = ({
  ids,
  id,
  disabled,
  onSuccess,
  variant = "ghost",
}: DeleteTableProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const deleteTable = async (tableId: string) => {
    await TablesService.deleteTable({ id: tableId });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const tableIds = ids || (id ? [id] : []);
      await Promise.all(tableIds.map((tableId) => deleteTable(tableId)));
    },
    onSuccess: () => {
      showSuccessToast(
        ids
          ? "테이블들이 성공적으로 삭제되었습니다."
          : "테이블이 성공적으로 삭제되었습니다."
      );
      setIsOpen(false);
      setHasSubmitted(false);
      onSuccess?.();
    },
    onError: () => {
      showErrorToast("테이블 삭제하는 동안 에러가 발생하였습니다.");
      setHasSubmitted(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "tables" || query.queryKey[0] === "tablesList",
      });
    },
  });

  const onSubmit = async () => {
    if (hasSubmitted || (!ids && !id)) return;
    setHasSubmitted(true);
    mutation.mutate();
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        {variant === "ghost" ? (
          <Button
            variant="ghost"
            size="sm"
            colorPalette="red"
            disabled={disabled || (!ids && !id)}
          >
            <FiTrash2 fontSize="16px" />
            테이블 삭제
          </Button>
        ) : (
          <Button value="add-item" my={4} disabled={disabled || (!ids && !id)}>
            <FaMinus fontSize="16px" />
            테이블 삭제
          </Button>
        )}
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {ids ? "선택 테이블 삭제" : "테이블 삭제"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>
              {ids
                ? `선택한 ${ids.length}개의 테이블은 영구적으로 삭제됩니다. 계속하시겠습니까? 이 작업은 되돌릴 수 없습니다.`
                : "이 테이블은 영구적으로 삭제됩니다. 계속하시겠습니까? 이 작업은 되돌릴 수 없습니다."}
            </Text>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                취소
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              colorPalette="red"
              type="submit"
              disabled={isSubmitting || hasSubmitted}
              loading={isSubmitting}
            >
              삭제
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteTable;
